/**
 * Entity Event Bus — decouples entity mutations from UI refresh.
 *
 * ANY mutation source (Skerpa, REST, import) emits events here.
 * ANY view subscribes to the entity types it cares about.
 *
 * Usage:
 *   // After mutating an entity (in any hook/component):
 *   entityEvents.emit('entity-created', { entityType: 'Competitor', label: 'Notion', id: 'abc-123' })
 *
 *   // In a view that displays Competitors:
 *   useEntityListener('entity-created', 'Competitor', () => refetchCompetitors())
 *   useEntityListener('entity-updated', 'Competitor', () => refetchCompetitors())
 *   useEntityListener('entity-deleted', 'Competitor', () => refetchCompetitors())
 *
 *   // Or subscribe to ALL entity types:
 *   useEntityListener('entity-created', '*', () => refetchEverything())
 */

export type EntityEventType = 'entity-created' | 'entity-updated' | 'entity-deleted'

export interface EntityEventDetail {
  entityType: string
  label?: string
  id?: string
  section?: string
  source?: 'skerpa' | 'user' | 'import' | 'system'
  [key: string]: unknown
}

type Listener = (detail: EntityEventDetail) => void

const listeners = new Map<string, Set<Listener>>() // key: "event:entityType" or "event:*"

function key(event: EntityEventType, entityType: string) {
  return `${event}:${entityType.toLowerCase()}`
}

export const entityEvents = {
  on(event: EntityEventType, entityType: string, listener: Listener): () => void {
    const k = key(event, entityType)
    if (!listeners.has(k)) listeners.set(k, new Set())
    listeners.get(k)!.add(listener)
    return () => { listeners.get(k)?.delete(listener) }
  },

  emit(event: EntityEventType, detail: EntityEventDetail) {
    // Notify type-specific listeners
    const typeKey = key(event, detail.entityType)
    listeners.get(typeKey)?.forEach(fn => { try { fn(detail) } catch { /* */ } })

    // Notify wildcard listeners
    const wildKey = key(event, '*')
    listeners.get(wildKey)?.forEach(fn => { try { fn(detail) } catch { /* */ } })
  },
}

// ── Legacy compatibility: re-export as skerpaActionBus ──
// Existing code imports skerpaActionBus — this adapter preserves backward compat
export const skerpaActionBus = {
  on(action: EntityEventType, listener: Listener) {
    return entityEvents.on(action, '*', listener)
  },
  emit(action: EntityEventType, detail: EntityEventDetail) {
    entityEvents.emit(action, { ...detail, source: detail.source || 'skerpa' })
  },
}
